/// <amd-module name="Wit/Common/QuerySaveDialog/Components/QueryItemDropdown" />
import * as React from "react";
import { IReadonlyObservableArray, ObservableArray } from "VSS/Core/Observable";
import { useComponentContext } from "VSS/Platform/Layout";
import { IListBoxItem } from "VSSUI/ListBox";
import { getSafeId } from "VSSUI/Util";
import { IQueryListService } from "Wit/Common/QueryListServices/IQueryListService";
import { EmptyItem, QueryItem } from "Wit/Common/QueryUtils/Models";
import { FolderDropdown, IFolderTreeDropdownProps } from "./FolderDropdown";
import { ITreeItem } from "VSSUI/Utilities/TreeItemProvider";

export interface IQueryItemDropdownProps extends Omit<IFolderTreeDropdownProps, "items"> {
    /**
     * Items should be supplied as an ObservableArray if they will be changing. Allows empty list box items so loading cell can be passed in.
     */
    items?: IReadonlyObservableArray<IListBoxItem<QueryItem | EmptyItem>> | IListBoxItem<QueryItem | EmptyItem>[];
    projectId?: string | undefined;
    disabled?: boolean | undefined;
}

const wrappedItems = new ObservableArray<IListBoxItem<QueryItem | EmptyItem>>([]);

export const QueryItemDropdown: React.FunctionComponent<IQueryItemDropdownProps> = props => {
    const { items,  onValueChange, projectId, disabled, ...rest } = props;
    const { pageContext } = useComponentContext();

    const listService = pageContext.getService<IQueryListService>("IQueryListService");

    const loadQueryItems = async () => {
        if (!listService.queryTree || listService.queryTree.length === 0) {
            if (projectId) {
                await listService.getSharedQueriesByProjectIDAsync(projectId);
            } else {
                await listService.getQueriesAsync();
            }
        }

        if (listService.queryTree.length > 0) {
            const roots = listService.queryTree.roots.map((item: ITreeItem<QueryItem | EmptyItem>) => {
                return item.data;
            });
            if (roots) {
                wrappedItems.value = convertQueriesToListBoxItems(roots);
            }
            return true;
        }
    };

    React.useEffect(() => {
        if (!projectId && !items){
                loadQueryItems();
            
        }
    }, [listService.isLoading, listService.queryTree]);

    React.useEffect(() => {
        if (projectId){
            itemsRefreshManually(projectId);
        }
    }, [projectId]);

    const toggleTreeItem = (queryItem: IListBoxItem<QueryItem>) => {
        // we know this is a QueryItem because we are toggling it
        const item = queryItem.data as QueryItem;
        if (queryItem.expanded && item.hasChildren && !item.children) {
            // add in a loading spinner while we get the children
            const emptyItem = {
                id: getSafeId(queryItem.id + "-loading") ?? "",
                data: { isEmptyFolderContext: true, isLoadingContext: true } as EmptyItem,
                parent: queryItem
            };
            wrappedItems.splice(wrappedItems.value.findIndex(item => item.id === queryItem.id) + 1, 1, emptyItem);
            queryItem.expanded = true;
            // get the children
            getQueryChildren(queryItem);
        }
    };

    const getQueryChildren = async (queryItem: IListBoxItem<QueryItem>) => {
        const queryWithChildren = await listService.getQueryWithChildren(queryItem.data!);
        const queryTreeItems = convertQueriesToListBoxItems([queryWithChildren], queryItem.parent);
        queryTreeItems[0].expanded = true;
        wrappedItems.splice(
            wrappedItems.value.findIndex(item => item.id == queryItem.id),
            2,
            ...queryTreeItems
        );
    };

    const itemsRefreshManually = async (projectID: string) => {
        await listService.getSharedQueriesByProjectIDAsync(projectID);

        if (listService.queryTree.length > 0) {
            const roots = listService.queryTree.roots.map((item: ITreeItem<QueryItem | EmptyItem>) => {
                return item.data;
            });
            if (roots) {
                wrappedItems.value = convertQueriesToListBoxItems(roots);
            }
        }
    };
    return (
        <FolderDropdown
            items={wrappedItems}
            onToggled={(event, item) => {
                if (item.data?.isEmptyFolderContext) {
                    return;
                }
                toggleTreeItem(item as IListBoxItem<QueryItem>);
            }}
            onValueChange={async value => {
                if (onValueChange) {
                    onValueChange(value);
                }
            }}
            disabled={disabled}
            {...rest}
        />
    );
};

function convertQueriesToListBoxItems(queries: (QueryItem | EmptyItem)[], parent?: IListBoxItem<QueryItem>) {
    const treeItems: IListBoxItem<QueryItem | EmptyItem>[] = [];
    if (queries) {
        const folders = queries.filter((query: QueryItem) => {
            return query.isFolder;
        });
        if (folders) {
            folders.forEach((folder: QueryItem) => {
                const item: IListBoxItem<QueryItem> = { id: folder.id, expanded: !parent, data: folder, parent, text: folder.path };
                const items: IListBoxItem<QueryItem | EmptyItem>[] = [item];
                if (folder.hasChildren) {
                    items.push(...convertQueriesToListBoxItems(folder.children as QueryItem[], item));
                }
                treeItems.push(...items);
            });
        }
    } else {
        treeItems.push({
            id: getSafeId(parent?.id) ?? "",
            data: { isEmptyFolderContext: true, isLoadingContext: false },
            expanded: false,
            parent: parent
        });
    }

    return treeItems;
}
